// src/pages/CampaignFlow/phases/Phase4Dashboard.jsx
import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { ChartBarIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import apiClient from '../../../lib/apiClient';

export default function Phase4Dashboard({ campaignId }) {
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading]         = useState(true);
  const [cloning, setCloning]         = useState(false);
  const [toast, setToast]             = useState('');
  const navigate = useNavigate();

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  useEffect(() => {
    const load = async () => {
      try {
        const r = await apiClient.request(`/campaign/${campaignId}/performance`, { method: 'GET' });
        const d = await r.json();
        if (d.success && d.performance) setPerformance(d.performance);
      } catch {}
      finally { setLoading(false); }
    };
    load();
    const interval = setInterval(load, 30000); // Refresh a cada 30s
    return () => clearInterval(interval);
  }, [campaignId]);

  const handleClone = async () => {
    setCloning(true);
    try {
      const r = await apiClient.request(`/campaign/${campaignId}/clone`, { method: 'POST' });
      const d = await r.json();
      if (d.success) {
        showToast('Campanha clonada! Redirecionando...');
        setTimeout(() => navigate(`/campaign-flow?campaign=${d.campaign_id}&step=profile`), 1500);
      }
    } catch {
      showToast('Erro ao clonar campanha');
    } finally {
      setCloning(false);
    }
  };

  // Monta dados do Recharts a partir da performance do backend
  const chartData = performance?.dates?.map((date, i) => ({
    date,
    Cliques:    performance.clicks?.[i]      ?? 0,
    Impressões: performance.impressions?.[i] ?? 0,
    Vendas:     performance.sales?.[i]       ?? 0,
  })) || [];

  const totalClicks      = performance?.clicks?.reduce((a, b) => a + b, 0) ?? 0;
  const totalImpressions = performance?.impressions?.reduce((a, b) => a + b, 0) ?? 0;
  const totalSales       = performance?.sales?.reduce((a, b) => a + b, 0) ?? 0;
  const ctr              = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(1) : '0.0';

  return (
    <div className="cf-phase cf-phase--dashboard">
      {toast && <div className="cf-toast">{toast}</div>}

      <div className="cf-mascot-row">
        <ChartBarIcon className="cf-mascot" style={{ width: 32, height: 32 }} />
        <div className="cf-mascot-speech">
          Sua campanha está no ar! Aqui estão os resultados em tempo real.
        </div>
      </div>

      {/* KPIs */}
      <div className="cf-kpi-row">
        <div className="cf-kpi-card cf-glass-card">
          <div className="cf-kpi-card__value">{totalImpressions.toLocaleString('pt-BR')}</div>
          <div className="cf-kpi-card__label">Impressões</div>
        </div>
        <div className="cf-kpi-card cf-glass-card">
          <div className="cf-kpi-card__value">{totalClicks.toLocaleString('pt-BR')}</div>
          <div className="cf-kpi-card__label">Cliques</div>
        </div>
        <div className="cf-kpi-card cf-glass-card cf-kpi-card--accent">
          <div className="cf-kpi-card__value">{ctr}%</div>
          <div className="cf-kpi-card__label">CTR</div>
        </div>
        <div className="cf-kpi-card cf-glass-card cf-kpi-card--green">
          <div className="cf-kpi-card__value">{totalSales}</div>
          <div className="cf-kpi-card__label">Vendas</div>
        </div>
      </div>

      {/* Gráfico Recharts */}
      <div className="cf-chart-card cf-glass-card">
        <h3 className="cf-chart-title"><ChartBarIcon style={{ width: 18, height: 18, display: 'inline', marginRight: 6 }} />Performance — Últimos 7 dias</h3>
        {loading ? (
          <div className="cf-loading-sm">Carregando dados...</div>
        ) : chartData.length === 0 ? (
          <div className="cf-empty">Nenhum dado de performance ainda.</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" />
              <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
              <YAxis tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
              <Tooltip
                contentStyle={{ background: 'rgba(15,23,42,0.95)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: '10px' }}
                labelStyle={{ color: '#a5b4fc' }}
              />
              <Legend wrapperStyle={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px' }} />
              <Line type="monotone" dataKey="Impressões" stroke="#6366f1" strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="Cliques"    stroke="#22d3ee" strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="Vendas"     stroke="#4ade80" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Botão flutuante Repetir Campanha */}
      <div className="cf-float-actions">
        <button className="cf-btn cf-btn--repeat" onClick={handleClone} disabled={cloning}>
          {cloning ? 'Clonando...' : <><ArrowPathIcon style={{ width: 16, height: 16, display: 'inline', marginRight: 4 }} />Repetir Campanha</>}
        </button>
      </div>
    </div>
  );
}
